import React from "react";

/**
 * SampleRecordsPanel — the one renderer for "show me the actual rows".
 *
 * Extracted from the rule-details modal so the Rule Studio can present a candidate option's evidence in
 * exactly the same shape the SME already knows: a table of the fields that belong to the rule, failing
 * rows first, so she can check the engine's verdict against the data rather than trusting a count.
 */
export interface SampleSets {
  fails?: any[];
  passes?: any[];
}

/** Columns worth showing: the rule's own fields first, then anything else present on the record. */
export function sampleColumns(rows: any[], ruleFields?: string[], max = 8): string[] {
  const present = Object.keys(rows.find((r) => r && typeof r === "object") || {});
  if (!present.length) return [];
  const leaf = (f: string) => String(f).split("::").pop()!.toUpperCase();
  const wanted = (ruleFields || []).map(leaf);
  const first = present.filter((c) => wanted.includes(c.toUpperCase()));
  const rest = present.filter((c) => !wanted.includes(c.toUpperCase()));
  // key columns (MATNR/WERKS/VKORG…) are what makes a row identifiable, so keep them even if the rule
  // does not name them explicitly
  const keyish = rest.filter((c) => /MATNR|WERKS|VKORG|VTWEG|MATKL/i.test(c));
  const others = rest.filter((c) => !keyish.includes(c));
  return [...keyish, ...first, ...others].slice(0, max);
}

export function SampleTable(
  { rows, kind, ruleFields, limit = 20 }:
  { rows: any[]; kind: "fail" | "pass"; ruleFields?: string[]; limit?: number }
) {
  if (!rows?.length) return null;
  const cols = sampleColumns(rows, ruleFields);
  return (
    <div className="sr-tbl">
      <div className={"sr-k " + kind}>
        {kind === "fail" ? "Failing records" : "Passing records"} <span className="n">({rows.length})</span>
      </div>
      <div className="sr-scroll">
        <table>
          <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>
            {rows.slice(0, limit).map((row, i) => (
              <tr key={i} className={kind}>
                {cols.map((c) => <td key={c}>{row?.[c] == null || row?.[c] === "" ? "∅" : String(row[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit && <div className="sr-more">showing {limit} of {rows.length}</div>}
    </div>
  );
}

export default function SampleRecordsPanel(
  { title, subtitle, fails, passes, ruleFields, onClose }:
  { title: string; subtitle?: string; fails?: any[]; passes?: any[];
    ruleFields?: string[]; onClose: () => void }
) {
  const f = fails || [], p = passes || [];
  return (
    <div className="sr-scrim" onClick={onClose}>
      <div className="sr" onClick={(e) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header>
          <div>
            <div className="eyebrow">SAMPLE RECORDS</div>
            <div className="title">{title}</div>
            {subtitle && <div className="sub">{subtitle}</div>}
          </div>
          <button className="x" onClick={onClose}>×</button>
        </header>
        <div className="sr-body">
          {!f.length && !p.length && <div className="mut">No sample records were returned for this run.</div>}
          <SampleTable rows={f} kind="fail" ruleFields={ruleFields} />
          <SampleTable rows={p} kind="pass" ruleFields={ruleFields} />
        </div>
      </div>
    </div>
  );
}

const CSS = `
.sr-scrim{position:fixed;inset:0;z-index:94;background:rgba(14,36,54,.42);display:flex;align-items:center;justify-content:center;padding:24px}
.sr{--line:#e3e8ee;--muted:#6b7c8d;width:min(1000px,96vw);max-height:90vh;background:#fff;border-radius:16px;
 display:flex;flex-direction:column;overflow:hidden;box-shadow:0 28px 70px rgba(0,0,0,.3);color:#0E2436}
.sr header{display:flex;align-items:flex-start;justify-content:space-between;padding:14px 20px;background:#0e2436;color:#fff}
.sr .eyebrow{font-size:10px;letter-spacing:.12em;color:#9db4c9}
.sr .title{font-size:15px;font-weight:700;font-family:'Space Grotesk',system-ui,sans-serif}
.sr .sub{font-size:11.5px;color:#9db4c9;margin-top:2px;font-family:ui-monospace,monospace}
.sr .x{all:unset;cursor:pointer;font-size:24px;color:#cdd8ea}.sr .x:hover{color:#fff}
.sr-body{overflow:auto;padding:14px 20px 20px}
.sr .mut{color:var(--muted);font-size:13px}
.sr-tbl{margin-bottom:16px}
.sr-k{font-size:11px;letter-spacing:.06em;text-transform:uppercase;font-weight:700;margin-bottom:6px}
.sr-k.fail{color:#b23a30}.sr-k.pass{color:#2f7a4f}
.sr-k .n{color:var(--muted);font-weight:400}
.sr-scroll{overflow-x:auto;border:1px solid var(--line);border-radius:8px}
.sr table{font-size:12px;border-collapse:collapse;width:100%}
.sr th{text-align:left;padding:5px 10px;border-bottom:1px solid var(--line);white-space:nowrap;
 background:#f6f8fa;font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;color:#5a6675}
.sr td{padding:4px 10px;border-bottom:1px solid #f2f4f7;white-space:nowrap;font-family:ui-monospace,monospace}
.sr tr.fail td:first-child{box-shadow:inset 3px 0 0 #e5b4ae}
.sr tr.pass td:first-child{box-shadow:inset 3px 0 0 #b7ddc6}
.sr-more{font-size:11.5px;color:var(--muted);margin-top:5px}
`;
